import React, { useState, useEffect, useRef } from 'react';
import styles from '../styles/ChatModal.module.css';
import messagingService from '../../services/MessagingService';

const ChatModal = ({ isOpen, onClose, userName, patientId }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  const suggestedQuestions = [
    "Can you tell me about my progress this week?",
    "Can you tell me how much time I've spent doing extra exercises this week?",
    "What's my range of motion improvement?",
    "Which exercise am I doing best at?"
  ];

  useEffect(() => {
    if (isOpen && patientId) {
      loadMessages();
      // Subscribe to real-time messages from SuperAdmin
      const unsubscribe = messagingService.onNewMessage((newMessage) => {
        console.log('Real-time message received:', newMessage);
        // Check if message is for this patient
        if (newMessage.recipient_id === patientId || newMessage.sender_id === patientId) {
          console.log('Message is for current patient, adding to conversation');
          setMessages(prev => [...prev, {
            text: newMessage.message,
            sender: newMessage.sender_type === 'mendy' ? 'mendy' : 'user',
            timestamp: new Date(newMessage.created_at)
          }]);
        }
      });
      return () => unsubscribe();
    }
  }, [isOpen, patientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      if (!patientId) {
        console.error('No patient ID provided');
        return;
      }

      console.log('Loading messages for patient:', patientId);

      // Use the new getPatientConversationForPatient method
      const conversationHistory = await messagingService.getPatientConversationForPatient(patientId);

      console.log('Loaded messages:', conversationHistory?.length || 0);

      if (conversationHistory && conversationHistory.length > 0) {
        // Convert database messages to UI format
        const formattedMessages = conversationHistory.map(msg => ({
          text: msg.message,
          sender: msg.sender_type === 'mendy' ? 'mendy' : 'user',
          timestamp: new Date(msg.created_at)
        }));
        setMessages(formattedMessages);
        setShowSuggestions(false);
      } else {
        // No previous messages, show welcome message
        setMessages([
          {
            text: `Hi ${userName}! I'm Mendy, your AI rehabilitation coach. I'm here to help you track your progress and answer any questions about your exercises. How can I help you today?`,
            sender: 'mendy',
            timestamp: new Date()
          }
        ]);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      // Fallback to welcome message on error
      setMessages([
        {
          text: `Hi ${userName}! I'm Mendy, your AI rehabilitation coach. I'm here to help you track your progress and answer any questions about your exercises. How can I help you today?`,
          sender: 'mendy',
          timestamp: new Date()
        }
      ]);
      setShowSuggestions(true);
    }
  };

  const getMendyResponse = (userInput) => {
    const input = userInput.toLowerCase();

    // Check for specific keywords and provide contextual responses
    if (input.includes('progress') && input.includes('week')) {
      return "Great question! This week you've completed 12 exercise sessions with an average accuracy of 87%. Your form has improved by 15% compared to last week. You're making excellent progress!";
    }

    if ((input.includes('extra') || input.includes('much time')) && (input.includes('exercise') || input.includes('spent'))) {
      return "Of course! You've put in a lot of extra work this week. You've had 5 extra exercise sessions this week, totaling an extra 65 minutes. Keep up the good work! Your dedication is really showing in your improved range of motion.";
    }

    if (input.includes('how') && input.includes('doing')) {
      return "You're doing wonderfully! Your consistency has been impressive - you've maintained a 5-day streak and your form accuracy has increased to 89%. The Apple Pick exercise seems to be your strongest with a 92% completion rate.";
    }

    if (input.includes('pain') || input.includes('hurt') || input.includes('sore')) {
      return "I understand you may be experiencing some discomfort. It's normal to feel some soreness as your body adapts. Remember to warm up properly and consider reducing the intensity slightly. If pain persists, please consult with your clinician. Your safety is our priority.";
    }

    if (input.includes('difficult') || input.includes('hard') || input.includes('struggling')) {
      return "It's completely normal to find some exercises challenging. I've noticed you're doing great with the Garden Game but finding the Row Adventure more difficult. Would you like me to suggest some modifications? Remember, progress isn't always linear, and you're doing better than you think!";
    }

    if (input.includes('today') || input.includes('session')) {
      return "Today you completed 3 exercises with an average duration of 12 minutes. Your form accuracy was 85%, which is above your weekly average! The Sky Flight exercise showed particular improvement. Ready for your next session?";
    }

    if (input.includes('improve') || input.includes('better')) {
      return "To improve further, I recommend focusing on consistency and form over speed. Your data shows you perform best in the morning sessions. Try maintaining a steady pace and remember to breathe properly during exercises. You've already improved 20% since you started!";
    }

    if (input.includes('favorite') || input.includes('like')) {
      return "Based on your performance data, you seem to excel at the Apple Pick exercise with a 95% completion rate! You also show strong engagement with the Garden Game. These exercises are perfectly suited to your current range of motion. Keep enjoying them!";
    }

    if (input.includes('range') || input.includes('rom')) {
      return "Your range of motion has improved significantly! Your shoulder flexion has increased from 145° to 165° (normal range is 150-180°). Your elbow flexion is now at 145°, which is within the normal range. This is excellent progress!";
    }

    if (input.includes('thank') || input.includes('thanks')) {
      return "You're very welcome! It's my pleasure to support your recovery journey. Remember, you're doing amazing work. Every session counts, and your dedication is inspiring. Is there anything else you'd like to know about your progress?";
    }

    // Default responses for general queries
    const generalResponses = [
      "That's a great observation! Your consistency this week has been remarkable. You've maintained your exercise routine for 5 consecutive days.",
      "I'm here to support you every step of the way. Your progress data shows steady improvement across all metrics.",
      "Remember, recovery is a journey, not a race. You're making consistent progress, and that's what matters most.",
      "Your dedication is showing results! This week's performance is 18% better than last week.",
      "Keep up the excellent work! Your form accuracy and completion rates are improving steadily."
    ];

    return generalResponses[Math.floor(Math.random() * generalResponses.length)];
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend.trim()) return;

    const userMessage = {
      text: textToSend,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(false); // Don't show loading since we're not expecting immediate AI response
    setShowSuggestions(false); // Hide suggestions after first message

    // Send message to database - SuperAdmin will see it and respond
    try {
      await messagingService.replyToMendy(textToSend);
      console.log('Message sent to SuperAdmin');

      // Note: SuperAdmin will respond when they see the message
      // The response will appear via real-time subscription
    } catch (error) {
      console.error('Error sending message:', error);
      // Show error message to user
      setMessages(prev => [...prev, {
        text: "Sorry, I'm having trouble sending your message. Please check your connection and try again.",
        sender: 'mendy',
        timestamp: new Date()
      }]);
    }
  };

  const handleSendClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSendMessage();
  };

  const handleSuggestionClick = (question) => {
    handleSendMessage(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <img src="/dashboard/mendy.svg" alt="Mendy" className={styles.mendyIcon} />
            <div>
              <h2>Chat with Mendy</h2>
              <span className={styles.status}>AI Coach • Always Here to Help</span>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.chatContainer}>
          <div className={styles.messagesContainer}>
            {showSuggestions && messages.length === 1 && (
              <div className={styles.suggestionsContainer}>
                <p className={styles.suggestionsTitle}>Suggested Questions:</p>
                <div className={styles.suggestionsList}>
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      className={styles.suggestionButton}
                      onClick={() => handleSuggestionClick(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={`${styles.messageWrapper} ${
                  message.sender === 'user' ? styles.userMessage : styles.mendyMessage
                }`}
              >
                {message.sender === 'mendy' && (
                  <img src="/dashboard/mendy.svg" alt="Mendy" className={styles.messageAvatar} />
                )}
                <div className={styles.messageBubble}>
                  <p>{message.text}</p>
                  <span className={styles.timestamp}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className={styles.mendyMessage}>
                <img src="/dashboard/mendy.svg" alt="Mendy" className={styles.messageAvatar} />
                <div className={styles.typingIndicator}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className={styles.inputContainer}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className={styles.messageInput}
            />
            <button
              onClick={handleSendClick}
              onTouchEnd={handleSendClick}
              disabled={!inputMessage.trim() || isLoading}
              className={styles.sendButton}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 2L11 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 2L15 22L11 13L2 9L22 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <p className={styles.privacyNote}>
            Your conversations are private and secure. Mendy is here to support your recovery journey.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;